import React, { useState } from 'react';
import {
  Box, Drawer, Typography, Avatar, IconButton, Menu, MenuItem, ListItemIcon,
  ButtonBase, useTheme, useMediaQuery,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase-config';
import { SUPPORT_WHATSAPP } from '../utils/enums';
import { GUEST_EXAM_LIMIT, guestExamsLeft } from '../utils/guestExams';

const SIDEBAR_WIDTH = 264;

const PRACTICE_LINKS = [
  { to: '/home', label: 'Home', Icon: HomeOutlinedIcon },
  { to: '/practice-exam', label: 'Practice Exam', Icon: NoteAddOutlinedIcon },
  { to: '/scheduled-exams', label: 'Scheduled Exams', Icon: AssignmentOutlinedIcon },
  { to: '/profile', label: 'My Profile', Icon: PersonOutlineIcon },
];

const SectionTitle = ({ children }) => (
  <Typography sx={{ px: 2.5, pt: 3, pb: 1, fontSize: '13px', fontWeight: 600, color: '#94a3b8' }}>
    {children}
  </Typography>
);

function NavItem({ label, icon, active, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      sx={{
        width: 'calc(100% - 16px)', mx: 1, px: 1.5, py: 1.25, mb: 0.5,
        justifyContent: 'flex-start', gap: 1.75, borderRadius: '10px',
        color: active ? '#ffffff' : '#cbd5e1',
        bgcolor: active ? 'rgba(148,163,184,0.18)' : 'transparent',
        boxShadow: active ? 'inset 3px 0 0 #f97316' : 'none',
        fontSize: '15px', fontWeight: active ? 600 : 500, fontFamily: 'inherit',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'rgba(148,163,184,0.12)', color: '#ffffff' },
      }}
    >
      {icon}
      {label}
    </ButtonBase>
  );
}

export default function AppLayout({ onLogout }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isDesktop = useMediaQuery(useTheme().breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const user = auth.currentUser;
  const isGuest = Boolean(user?.isAnonymous);
  const name = isGuest ? 'Guest' : (user?.displayName || user?.email?.split('@')[0] || 'Student');
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const go = (to) => { navigate(to); setMobileOpen(false); };

  const sidebar = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#0f172a', color: '#e2e8f0' }}>
      <Box sx={{ p: 2 }}>
        <ButtonBase
          onClick={() => go('/home')}
          aria-label="Home"
          sx={{ bgcolor: '#ffffff', borderRadius: '12px', px: 1.5, py: 0.5, width: '100%', justifyContent: 'flex-start' }}
        >
          <Box component="img" src="/educonnect_logo.jpg" alt="Educonnect" sx={{ height: 44 }} />
        </ButtonBase>
      </Box>

      <Box component="nav" sx={{ flex: 1, overflowY: 'auto' }}>
        <SectionTitle>Prep &amp; Practice</SectionTitle>
        {PRACTICE_LINKS.map(({ to, label, Icon }) => (
          <NavItem
            key={to}
            label={label}
            icon={<Icon sx={{ fontSize: 22 }} />}
            active={pathname === to}
            onClick={() => go(to)}
          />
        ))}
      </Box>

      <Box sx={{ borderTop: '1px solid rgba(148,163,184,0.15)', p: 1 }}>
        <ButtonBase
          component="a"
          href={`https://wa.me/91${SUPPORT_WHATSAPP}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Need help? Chat with support on WhatsApp, ${SUPPORT_WHATSAPP}`}
          sx={{
            width: '100%', mb: 1, p: 1.25, gap: 1.5, justifyContent: 'flex-start', textAlign: 'left',
            borderRadius: '12px', fontFamily: 'inherit',
            bgcolor: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.12)',
            transition: 'background-color 0.15s, border-color 0.15s',
            '&:hover': { bgcolor: 'rgba(148,163,184,0.14)', borderColor: 'rgba(148,163,184,0.22)' },
            '&:hover .support-arrow': { color: '#f1f5f9', transform: 'translate(1px, -1px)' },
            '&:focus-visible': { outline: '2px solid #25D366', outlineOffset: 2 },
          }}
        >
          <Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: 'rgba(37,211,102,0.14)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#f1f5f9', lineHeight: 1.3 }}>Need help?</Typography>
            <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums', lineHeight: 1.4 }}>
              +91 {SUPPORT_WHATSAPP.slice(0, 5)} {SUPPORT_WHATSAPP.slice(5)}
            </Typography>
          </Box>
          <ArrowOutwardIcon className="support-arrow" sx={{ fontSize: 16, color: '#64748b', transition: 'color 0.15s, transform 0.15s' }} />
        </ButtonBase>
        {isGuest ? (
          <Box sx={{ p: 1.25 }}>
            <Typography sx={{ fontSize: '13px', color: '#94a3b8', mb: 1.25, lineHeight: 1.5 }}>
              {guestExamsLeft() > 0
                ? `You have ${guestExamsLeft()} of ${GUEST_EXAM_LIMIT} free exams left. Sign in to keep your results and practise without limits.`
                : `You've used your ${GUEST_EXAM_LIMIT} free exams. Sign in to keep practising.`}
            </Typography>
            <ButtonBase
              onClick={() => go('/login')}
              sx={{ width: '100%', py: 1.1, borderRadius: '10px', bgcolor: '#f97316', color: '#fff', fontSize: '14px', fontWeight: 700, fontFamily: 'inherit', '&:hover': { bgcolor: '#ea580c' } }}
            >
              Sign in / Sign up
            </ButtonBase>
          </Box>
        ) : (
        <ButtonBase
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label="Account menu"
          sx={{ width: '100%', p: 1.25, borderRadius: '10px', gap: 1.5, justifyContent: 'flex-start', '&:hover': { bgcolor: 'rgba(148,163,184,0.12)' } }}
        >
          <Avatar src={user?.photoURL} sx={{ width: 36, height: 36, bgcolor: '#334155', fontSize: '14px', fontWeight: 700 }}>
            {initials}
          </Avatar>
          <Typography sx={{ flex: 1, textAlign: 'left', fontSize: '15px', fontWeight: 600, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name}
          </Typography>
          <KeyboardArrowDownIcon sx={{ color: '#94a3b8' }} />
        </ButtonBase>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop || mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isDesktop ? SIDEBAR_WIDTH : 0, flexShrink: 0,
          '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, border: 'none', boxSizing: 'border-box' },
        }}
      >
        {sidebar}
      </Drawer>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Phones only: the sidebar is hidden, so keep a way to open it */}
        {!isDesktop && (
          <Box sx={{ px: 1, pt: 1 }}>
            <IconButton aria-label="Open menu" onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        <Box component="main" sx={{ flex: 1, px: { xs: 2, md: 5 }, pt: { xs: 1, md: 5 }, pb: { xs: 4, md: 6 } }}>
          <Outlet />
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { width: SIDEBAR_WIDTH - 24, borderRadius: '10px' } } }}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); go('/profile'); }}>
          <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
          My Profile
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onLogout(); }} sx={{ color: '#ef4444' }}>
          <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
}
